import React, { useState, useMemo } from 'react'
import Grid from '../../../../components/Grid/Grid'
import { createHtmlPortalNode, OutPortal } from 'react-reverse-portal'
import TextInput from '../../../../components/FormElements/TextInput'
import FiltersContext from '../../../../components/Filters/FiltersContext'
import { Vehicle } from '../../../../lib/types/Vehicle'
import { Button, DropdownButton, Info, Table } from '@demandlocal/components'
import { ReactComponent as IconChangeViewCard } from '../../../../images/icons/icon_change_view_card.svg'
import { ReactComponent as IconChangeViewList } from '../../../../images/icons/icon_change_view_list.svg'
import { createColumnHelper, Row } from '@tanstack/react-table'
import { useVehiclesListView } from '../../../../store/zustand/vehiclesListStore'
import { motion } from 'framer-motion'
import { formatCurrency } from '../../../../lib/utils/formatNumber'
import dayjs from 'dayjs'
import GoogleSheetsExport from '../../../../forms/Exports/GoogleSheetsExport'
import { useDealership } from '../../../../store/zustand/dealershipStore'
import { useGetSoldVehiclesList } from '../../../../api/dealership/inventory/useGetSoldVehiclesList'
import GridItemSoldVehicle from '../../../../components/Grid/Items/GridItemSoldVehicle'
import SoldVehiclePath from '../../../../components/Performance/SalesPaths/SoldVehiclePath'
import { ReactComponent as AddInventoryImage } from '../../../../images/add_inventory.svg'
import { isEmpty } from 'lodash'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import LoadingAnimation from '../../../../components/LoadingAnimation'

const columnHelper = createColumnHelper<
  Vehicle & {
    sale_date: Date
    buyer_name: string
  }
>()

const SoldVehiclesList = (): JSX.Element => {
  const { view, setView } = useVehiclesListView()
  const [filteredTotal, setFilteredTotal] = useState<number | undefined>(undefined)
  const [filters, setFilters] = useState<Partial<Vehicle & { sale_month: string }>>({})
  const [sorting, setSorting] = useState<Record<string, unknown>>({})
  const filtersPortalNode = useMemo(() => createHtmlPortalNode(), [])
  const [exportGoogleSheet, setExportGoogleSheet] = useState<boolean>(false)
  const dealership = useDealership()
  const { t } = useTranslation('routes')
  const { data, isLoading } = useGetSoldVehiclesList({ pageIndex: 1, pageSize: 1 })

  const inventoryColumns = useMemo(
    () => [
      columnHelper.accessor('vin_number', {
        header: 'VIN',
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <button
              className={'font-bold text-xs text-primary-persian-blue'}
              {...{
                onClick: (event) => {
                  event.preventDefault()
                  row.toggleExpanded()
                },
                style: { cursor: 'pointer' },
              }}>
              {row.original.vin_number}
            </button>
          ) : (
            row.original.vin_number
          )
        },
      }),

      columnHelper.display({
        id: 'vehicle',
        header: 'Vehicle',
        cell: (info) => (
          <span>
            {info.row.original.make} {info.row.original.model}
          </span>
        ),
      }),

      columnHelper.accessor('sale_date', {
        header: 'Sale Date',
        cell: (info) => dayjs(info.getValue()).format('MMM D, YYYY'),
      }),
      columnHelper.accessor('buyer_name', {
        header: 'Buyer',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('price', {
        header: 'Sale Price',
        cell: (info) => formatCurrency(Number(info.getValue())),
      }),
      columnHelper.accessor('vehicle_state', {
        header: 'Vehicle State',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('year', {
        header: 'Year',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('make', {
        header: 'Make',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('model', {
        header: 'Model',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('days_on_lot', {
        header: () => (
          <div className="flex flex-row items-center gap-2" id="header">
            <span id="days_on_lot">Days before Purchase</span>
            <Info message="Days seen on dealer's lot before sale" />
          </div>
        ),
        cell: (info) => info.getValue() ?? 'N/A',
      }),
    ],
    [],
  )

  const displayFilterCount = useMemo(() => {
    return Boolean(
      filteredTotal &&
        filteredTotal > 0 &&
        Object.values(filters).filter((f) => !!f)?.length > 0,
    )
  }, [filteredTotal, filters])

  if (isLoading) {
    return (
      <div className="my-auto mx-auto ">
        <LoadingAnimation title="Loading..." />
      </div>
    )
  }

  if (Number(data?.pagination.total) === 0) {
    return (
      <div
        className={
          'flex flex-col text-center my-auto mx-auto w-2/3 items-center justify-center px-10'
        }>
        <AddInventoryImage className="mx-auto" />
        <span className={'font-bold text-3xl font-Spartan pb-1 pt-8'}>
          Connect your DMS to see sold cars!
        </span>
        <span className={'text-base font-Spartan pt-1 pb-8'}>
          Without a connected DMS, you're missing out on seamless inventory updates. Get
          connected and stay ahead!
        </span>
        <Link to={`${t('dealership.customers.sources.create', { dealership })}/sales`}>
          <Button>Connect your DMS</Button>
        </Link>
      </div>
    )
  }

  return (
    <div className={'flex flex-col flex-1 pb-8'}>
      <div className="text-md rounded-xl p-2 bg-gainsboro-gray/40 border-2 mb-4">
        Track vehicles sold by your company, matched directly with your DMS for accuracy.
      </div>
      <div className={'pb-4 flex flex-row self-end '}>
        <div className={'flex flex-1 flex-row justify-between gap-4'}>
          <div className="flex flex-row gap-2 items-center">
            {displayFilterCount && (
              <motion.div
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                key={'filters-total'}
                className={
                  'h-5 bg-primary-persian-blue text-center flex items-center text-white p-1.5 rounded-md text-xs font-bold'
                }>
                <span>{filteredTotal} Vehicles</span>
              </motion.div>
            )}
            <div>
              <OutPortal node={filtersPortalNode} />
            </div>
            <DropdownButton
              onClick={(e) => setExportGoogleSheet(false)}
              options={[
                {
                  label: 'Google sheet',
                  onClick: (e) => setExportGoogleSheet(true),
                },
              ]}>
              Export
            </DropdownButton>
            <div className={'flex items-center gap-3'}>
              {view === 'grid' && (
                <div onClick={() => setView(view === 'grid' ? 'table' : 'grid')}>
                  <IconChangeViewList
                    className={
                      'cursor-pointer stroke-primary-xiketic fill-primary-xiketic'
                    }
                  />
                </div>
              )}
              {view === 'table' && (
                <div onClick={() => setView(view === 'table' ? 'grid' : 'table')}>
                  <IconChangeViewCard
                    className={'cursor-pointer stroke-primary-xiketic'}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {exportGoogleSheet && (
        <GoogleSheetsExport
          setExportGoogleSheet={setExportGoogleSheet}
          apiEndpoint={`/agencies/${dealership?.organization_id}/dealership/${dealership?.id}/inventory/sold-vehicles`}
          filters={filters}
          columns={inventoryColumns}
        />
      )}

      <FiltersContext
        defaultValues={filters}
        portalNode={filtersPortalNode}
        setFilters={setFilters}>
        <TextInput name={'raw_make'} placeholder={'Make'} label={'Make'} />
        <TextInput name={'raw_model'} placeholder={'Model'} label={'Model'} />
        <TextInput name={'vin_number'} placeholder={'VIN'} label={'VIN'} />
        <TextInput name={'year'} placeholder={'Year'} label={'Year'} />
        <TextInput
          name={'sale_month'}
          placeholder={'Sale Month'}
          label={'Sale Month'}
          type={'month'}
        />
      </FiltersContext>
      {view === 'grid' && (
        <Grid
          fetchFn={useGetSoldVehiclesList}
          gridComponent={GridItemSoldVehicle}
          filters={filters}
          sorting={sorting as Record<string, string | undefined>}
          setTotalRows={(v) => setFilteredTotal(v)}
        />
      )}
      {view === 'table' && (
        <Table
          isDownloadable
          emptyLabel={'No vehicles found'}
          columns={inventoryColumns}
          filters={{ ...filters }}
          fetchFn={useGetSoldVehiclesList}
          setTotalRows={(v) => setFilteredTotal(v)}
          defaultSorting={[{ id: 'sale_date', desc: true }]}
          getRowCanExpand={() => true}
          renderSubComponent={SoldVehiclePath}
        />
      )}
    </div>
  )
}

export default SoldVehiclesList
